'use client'

import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react'
import { authApiService as authService } from '@/lib/auth-api'
// No token utils needed, use Zustand store only
import { useAuthStore } from '@/stores/auth-store'

interface AuthContextType {
  user: any
  isAuthenticated: boolean
  loading: boolean
  isInitialized: boolean
  login: (username: string, password: string) => Promise<any>
  logout: () => void
  signup: (userData: any) => Promise<any>
  forgotPassword: (email: string) => Promise<any>
  resetPassword: (token: string, password: string) => Promise<any>
  refreshAuth: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mounted, setMounted] = React.useState(false)
  
  // Use Zustand store for auth state
  const {
    user,
    isAuthenticated,
    loading,
    isInitialized,
    initialize,
    login: storeLogin,
    logout: storeLogout,
    signup: storeSignup,
    forgotPassword: storeForgotPassword,
    resetPassword: storeResetPassword,
    refreshAuth: storeRefreshAuth
  } = useAuthStore()

  // Initialize auth on mount
  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (mounted) {
      initialize().then(() => {
        // After initialize, if isAuthenticated is true but accessToken is missing, fix it
        const { isAuthenticated, accessToken, setUser, setTokens, setLoading, setInitialized } = useAuthStore.getState();
        if (isAuthenticated && !accessToken) {
          setUser(null);
          setTokens(null, null);
          setLoading(false);
          setInitialized(true);
        }
      }).catch(error => {
        console.error('Auth initialization failed:', error)
      })
    }
  }, [mounted, initialize])

  const login = useCallback(async (username: string, password: string) => {
    try {
      console.log('AuthContext: Starting login process');
      const response = await storeLogin(username, password)
      console.log('AuthContext: Login response:', response);
      return response
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      throw error;
    }
  }, [storeLogin])

  const logout = useCallback(async () => {
    try {
      await storeLogout()
    } catch (error) {
      console.error('Logout error:', error)
    }
  }, [storeLogout])

  const signup = useCallback(async (userData: any) => {
    console.log('AuthContext signup called with:', userData);
    try {
      console.log('Calling authService.signup...');
      const response = await storeSignup(userData)
      console.log('AuthService signup response:', response);
      return response
    } catch (error) {
      console.error('AuthContext signup error:', error);
      throw error;
    }
  }, [storeSignup])

  const forgotPassword = useCallback(async (email: string) => {
    try {
      const response = await storeForgotPassword(email)
      return response
    } catch (error) {
      console.error('AuthContext forgotPassword error:', error);
      throw error;
    }
  }, [storeForgotPassword])

  const resetPassword = useCallback(async (token: string, password: string) => {
    try {
      const response = await storeResetPassword(token, password)
      return response
    } catch (error) {
      console.error('AuthContext resetPassword error:', error);
      throw error;
    }
  }, [storeResetPassword])

  const refreshAuth = useCallback(async () => {
    try {
      await storeRefreshAuth()
    } catch (error) {
      console.error('AuthContext refreshAuth error:', error);
      throw error;
    }
  }, [storeRefreshAuth])

  // Memoize the context value to prevent unnecessary re-renders
  const value = React.useMemo(() => ({
    user,
    isAuthenticated,
    loading,
    isInitialized,
    login,
    logout,
    signup,
    forgotPassword,
    resetPassword,
    refreshAuth
  }), [user, isAuthenticated, loading, isInitialized, login, logout, signup, forgotPassword, resetPassword, refreshAuth])

  // Don't render the provider during SSR
  if (typeof window === 'undefined') {
    return <>{children}</>
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 